<script setup lang="ts">
import { onMounted } from 'vue';
import type {
  RegisterSignInFormData,
  StrapiRegisterData,
} from './useRegisterSignInForm';
import { formData, resetFormData } from './useRegisterSignInForm';
import { useQuasar } from 'quasar';
import { useAuth } from 'src/boot/auth';
import RegisterSignInCard from '../../components/RegisterSignInCard/RegisterSignInCard.vue';
import { useRoute } from 'vue-router';
import type { StrapiProviders } from 'src/shared/composables/useStrapiApi';
import { useStrapiPost } from 'src/shared/composables/useStrapiApi';
import { useAccountSignIn } from '../../composables/useAccountSignIn';
import { useProviderSignIn } from '../../composables/useProviderSignIn';

const { name: routeName } = useRoute();
const $q = useQuasar();
const { signIn } = useAuth();
const { signInWithCredentials } = useAccountSignIn();
const { enabledProviders, providerSignIn } = useProviderSignIn();

onMounted(() => {
  resetFormData();
  formData.value.isRegister = routeName === 'users.register';
});

// Sign-in accepts any account: a school teacher or student who typed their
// credentials here is authenticated exactly as they would be on /school/login,
// and `signIn()` sends them straight into the school app. This page still shows
// only the parent/child flow — it just doesn't punish arriving at the wrong one.
// Only *registration* stays flow-specific: this form creates a parent account.
async function signInAccount(formData: RegisterSignInFormData) {
  await signInWithCredentials(
    formData.identifier ?? '',
    formData.password ?? '',
  );
}

async function register(formData: RegisterSignInFormData) {
  const { execute, error, data } = useStrapiPost('auth/local/register', {
    username: formData.email,
    email: formData.email,
    password: formData.password,
  });

  $q.loading.show();
  await execute().catch((e: Error) => console.log('execute', e));
  $q.loading.hide();

  if (error?.value || !data?.value) return;

  await signIn((data.value as StrapiRegisterData)?.jwt);
}
</script>

<template>
  <div>
    <RegisterSignInCard
      v-model="formData"
      :providers="enabledProviders"
      flat
      square
      class="full-width bg-transparent"
      @sign-in="(data: RegisterSignInFormData) => signInAccount(data)"
      @register="(data: RegisterSignInFormData) => register(data)"
      @provider="(provider: StrapiProviders) => providerSignIn(provider)"
    />
  </div>
</template>
